/**
 * The relay wire protocol the AI Workbench's `RelayClient`
 * (`src/control/relay.ts`) already speaks. HTTP only, no WebSocket:
 *
 *   POST /device/code      { workspaceId, name, deviceId?, kind?, tags?, manifest? } -> DeviceCode
 *                          (`kind: "node"`, `tags` and `manifest` only from a headless node's join)
 *   POST /device/token     { device_code }        -> TokenResponse | { error }
 *   POST /token            { refresh_token }      -> TokenResponse
 *   GET  /workbench/stream (bearer, SSE)          <- event: request  data: RelayRequest
 *   POST /workbench/respond { id, response }
 *   POST /workbench/events  { workspaceId, events: ControlEvent[] }
 */
import { z } from "zod";
import { controlEventSchema } from "./platform.js";
import { nodeManifestSchema } from "./nodeManifest.js";
/** Scopes a remote client may hold on a workbench. Same list as the workbench's `ALL_SCOPES`. */
export const CONTROL_SCOPES = [
    "agents:read",
    "agents:write",
    "prompts:write",
    "composer:write",
    "permissions:write",
    "teams:write",
    "tasks:read",
    "tasks:write",
    "memory:write",
    "memory:read",
    "settings:write",
    "git:write",
    "ui:view",
    "ui:control",
    "diagrams:read",
    "diagrams:write",
    "terminals:read",
    "terminals:write",
    "home:read",
    "shell:read",
    "shell:write",
];
/** Scopes a person must have signed in recently to use through a remote call: control of the screen, typing into a terminal or a node's shell. */
export const STEP_UP_SCOPES = new Set(["ui:control", "terminals:write", "shell:write"]);
/**
 * Scopes a device is never granted by default, only when a person asks for them: the screen (`ui:*`), terminals
 * (`terminals:*`, a shell on the machine; D343, ticked at `companion.pair.begin` or on the device's page) and a node's
 * console (`shell:*`, `node.shell.subscribe`, schemas/nodeShell.ts; nodes only).
 */
export const OPT_IN_SCOPES = new Set(["ui:view", "ui:control", "terminals:read", "terminals:write", "shell:read", "shell:write"]);
export const controlScopeSchema = z.enum(CONTROL_SCOPES);
export const deviceCodeRequestSchema = z.object({
    workspaceId: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    /** The desktop app's stable install id; when given, it becomes the device id on approval. */
    deviceId: z.string().min(1).max(100).optional(),
    /** `node`: a headless workbench joining (`mojoup-node join`); it stays pending until someone with `nodes:approve` approves. */
    kind: z.enum(["desktop", "node"]).optional(),
    /** Mesh policy tags the node proposes (`tag:gpu`); the approver may edit them. */
    tags: z.array(z.string().min(1).max(64)).max(32).optional(),
    /** The node's manifest, for the approver to review. */
    manifest: nodeManifestSchema.optional(),
    /**
     * DPoP (RFC 9449): the RFC 7638 thumbprint of the key this device will prove possession of. The
     * token poll must then carry a `DPoP` proof signed by that key, and the tokens come back bound to
     * it (`token_type: "DPoP"`). Without it the device gets Bearer tokens, as before.
     */
    dpop_jkt: z.string().regex(/^[A-Za-z0-9_-]{43}$/).optional(),
});
export const deviceCodeSchema = z.object({
    device_code: z.string(),
    user_code: z.string(),
    verification_uri: z.string().url(),
    verification_uri_complete: z.string().url().optional(),
    interval: z.number().int().positive().optional(),
    expires_in: z.number().int().positive().optional(),
});
export const deviceTokenRequestSchema = z.object({ device_code: z.string() });
export const refreshRequestSchema = z.object({ refresh_token: z.string() });
export const tokenResponseSchema = z.object({
    access_token: z.string(),
    refresh_token: z.string().optional(),
    expires_in: z.number().int().positive(),
    /** `DPoP` when the tokens are bound to the device's key: send `Authorization: DPoP <token>` with a fresh proof on every call. */
    token_type: z.enum(["Bearer", "DPoP"]).optional(),
    /** The device id the platform knows this workbench by; send it in fleet registration and heartbeats. */
    device_id: z.string().optional(),
    /** Scopes the approver granted. */
    scope: z.string().optional(),
});
export const tokenErrorSchema = z.object({
    error: z.enum(["authorization_pending", "slow_down", "expired_token", "access_denied", "invalid_grant", "invalid_dpop_proof"]),
    error_description: z.string().optional(),
});
/** A JSON-RPC 2.0 request the relay forwards to a workbench on behalf of a client. */
export const jsonRpcRequestSchema = z.object({
    jsonrpc: z.literal("2.0"),
    id: z.union([z.string(), z.number()]),
    method: z.string(),
    params: z.unknown().optional(),
});
export const jsonRpcResponseSchema = z.object({
    jsonrpc: z.literal("2.0"),
    id: z.union([z.string(), z.number(), z.null()]),
    result: z.unknown().optional(),
    error: z.object({ code: z.number().int(), message: z.string(), data: z.unknown().optional() }).optional(),
});
/** SSE `event: request` payload on `GET /workbench/stream`. */
export const relayRequestSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    scopes: z.array(controlScopeSchema),
    rpc: jsonRpcRequestSchema,
    /**
     * Which workspace of a desktop instance the request targets (root id, or
     * `{ root, target }`). Absent = the focused root. The workbench's relay
     * client does not read this yet; the desktop gateway needs it before the
     * relay transport can be routed through it (workbench roadmap row D8).
     */
    workspace: z.union([z.string(), z.object({ root: z.string(), target: z.string().optional() })]).optional(),
    /**
     * The platform's control JWS (X-Aioe-Signature on the mesh path) over `JSON.stringify(rpc)`, for
     * this device, the calling person and exactly `scopes` (the scopes the device was granted). A
     * workbench verifies it with `verifyControlRequest(signature, jwks, { deviceId, body: JSON.stringify(rpc) })`
     * and serves the call with the claims' scopes, not the envelope's. Absent from platforms before
     * the agent-nodes work.
     */
    signature: z.string().optional(),
});
/**
 * One input event for a workbench's window, in the frame's pixel space (the workbench scales it
 * to its window). Mirrors `MirrorInput` / `parseInput` in the workbench's `src/control/mirror.ts`.
 * Needs the `ui:control` scope, which `ui:view` never implies.
 */
const coord = z.number().finite().min(0).max(100_000);
export const mirrorInputSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("mouse"), action: z.enum(["down", "up", "move"]), x: coord, y: coord, button: z.enum(["left", "middle", "right"]).optional(), clickCount: z.number().int().min(1).max(3).optional() }),
    z.object({ type: z.literal("wheel"), x: coord, y: coord, deltaX: z.number().finite().default(0), deltaY: z.number().finite().default(0) }),
    z.object({ type: z.literal("key"), action: z.enum(["down", "up", "char"]), key: z.string().min(1).max(32), modifiers: z.array(z.enum(["shift", "control", "alt", "meta"])).max(4).optional() }),
]);
export const relayRespondSchema = z.object({
    id: z.string(),
    response: jsonRpcResponseSchema,
});
export const relayEventsSchema = z.object({
    workspaceId: z.string().min(1).max(200),
    events: z.array(controlEventSchema).max(1000),
});
//# sourceMappingURL=relay.js.map